import { createRng, type SeededRandom } from "./utils";

export type Vec2 = { x: number; y: number };
export type RadarTarget = { id: number; x: number; y: number; vx: number; vy: number };
export type ConflictEdge = [number, number];
export type RadarWave = {
  id: number;
  seed: number;
  targets: RadarTarget[];
  originalEdges: ConflictEdge[];
  minimumCoverSize: number;
  durationMs: number;
  startedAt: number;
};
export type RadarMathProblem = {
  seed: number;
  prompt: string;
  choices: number[];
  answerIndex: number;
};

export const RADAR_WIDTH = 1000;
export const RADAR_HEIGHT = 600;
export const RADAR_RADIUS = 15;

function edgePoint(edge: number, rng: SeededRandom): Vec2 {
  const margin = 18;
  if (edge === 0) return { x: -margin, y: rng.int(35, RADAR_HEIGHT - 35) };
  if (edge === 1) return { x: RADAR_WIDTH + margin, y: rng.int(35, RADAR_HEIGHT - 35) };
  if (edge === 2) return { x: rng.int(35, RADAR_WIDTH - 35), y: -margin };
  return { x: rng.int(35, RADAR_WIDTH - 35), y: RADAR_HEIGHT + margin };
}

function toward(id: number, start: Vec2, destination: Vec2, seconds: number): RadarTarget {
  return {
    id,
    x: start.x,
    y: start.y,
    vx: (destination.x - start.x) / seconds,
    vy: (destination.y - start.y) / seconds,
  };
}

export function closestApproach(
  a: RadarTarget,
  b: RadarTarget,
  horizonSeconds = 9,
): { time: number; distance: number } {
  const rx = b.x - a.x;
  const ry = b.y - a.y;
  const rvx = b.vx - a.vx;
  const rvy = b.vy - a.vy;
  const speedSquared = rvx * rvx + rvy * rvy;
  if (speedSquared < 0.0001) return { time: 0, distance: Math.hypot(rx, ry) };
  const time = Math.max(
    0,
    Math.min(horizonSeconds, -((rx * rvx) + (ry * rvy)) / speedSquared),
  );
  return { time, distance: Math.hypot(rx + rvx * time, ry + rvy * time) };
}

export function conflictEdges(
  targets: readonly RadarTarget[],
  horizonSeconds = 9,
): ConflictEdge[] {
  const edges: ConflictEdge[] = [];
  for (let first = 0; first < targets.length; first += 1) {
    for (let second = first + 1; second < targets.length; second += 1) {
      const a = targets[first]!;
      const b = targets[second]!;
      const cpa = closestApproach(a, b, horizonSeconds);
      // Scoring threshold: a predicted closest approach within 2.1 radii counts
      // as a conflict to resolve. This is intentionally larger than the live
      // physical-contact radius (1.9) used to register an actual collision.
      if (cpa.time > 0.15 && cpa.distance <= RADAR_RADIUS * 2.1) {
        edges.push([a.id, b.id]);
      }
    }
  }
  return edges;
}

export function coversEdges(
  removed: ReadonlySet<number>,
  edges: readonly ConflictEdge[],
): boolean {
  return edges.every(([a, b]) => removed.has(a) || removed.has(b));
}

export function minimumVertexCoverSize(
  ids: readonly number[],
  edges: readonly ConflictEdge[],
): number {
  if (!edges.length) return 0;
  const total = 1 << ids.length;
  for (let size = 1; size <= ids.length; size += 1) {
    for (let mask = 1; mask < total; mask += 1) {
      let bitCount = 0;
      for (let bit = 0; bit < ids.length; bit += 1) bitCount += (mask >> bit) & 1;
      if (bitCount !== size) continue;
      const selected = new Set<number>();
      ids.forEach((id, index) => {
        if ((mask >> index) & 1) selected.add(id);
      });
      if (coversEdges(selected, edges)) return size;
    }
  }
  return ids.length;
}

function randomDecoy(id: number, difficulty: number, rng: SeededRandom): RadarTarget {
  const startEdge = rng.int(0, 3);
  const endEdge = [1, 0, 3, 2][startEdge]!;
  const start = edgePoint(startEdge, rng);
  const destination = edgePoint(endEdge, rng);
  const travelSeconds = Math.max(4.2, 8.6 - difficulty * 0.55 + rng.next() * 1.2);
  return toward(id, start, destination, travelSeconds);
}

/**
 * Generates a fully reproducible wave. Same id, difficulty, and seed produce
 * byte-for-byte equivalent initial targets and scoring metadata.
 */
export function generateRadarWave(
  id: number,
  difficulty: number,
  seed = id * 1009 + difficulty * 9176,
): RadarWave {
  const rng = createRng(seed);
  const targetCount = Math.max(2, Math.min(7, difficulty + rng.int(1, 2)));
  const scenarioRoll = rng.next();
  const desiredConflicts = scenarioRoll < 0.18 ? 0 : scenarioRoll < 0.66 ? 1 : 2;

  for (let attempt = 0; attempt < 250; attempt += 1) {
    const ids = rng.shuffle([1, 2, 3, 4, 5, 6, 7, 8, 9]).slice(0, targetCount);
    const targets: RadarTarget[] = [];
    let cursor = 0;
    const useImmediate = difficulty >= 4 && rng.next() < 0.32;

    for (let pair = 0; pair < desiredConflicts && cursor + 1 < ids.length; pair += 1) {
      const nearEdge = useImmediate && pair === 0;
      const collisionPoint = nearEdge
        ? rng.pick([
            { x: rng.int(50, 130), y: rng.int(50, 150) },
            { x: rng.int(RADAR_WIDTH - 130, RADAR_WIDTH - 50), y: rng.int(50, 150) },
            { x: rng.int(50, 130), y: rng.int(RADAR_HEIGHT - 150, RADAR_HEIGHT - 50) },
            { x: rng.int(RADAR_WIDTH - 130, RADAR_WIDTH - 50), y: rng.int(RADAR_HEIGHT - 150, RADAR_HEIGHT - 50) },
          ])
        : {
            x: rng.int(170, RADAR_WIDTH - 170),
            y: rng.int(120, RADAR_HEIGHT - 120),
          };
      const firstEdge = rng.int(0, 3);
      let secondEdge = rng.int(0, 3);
      while (secondEdge === firstEdge) secondEdge = rng.int(0, 3);
      const arrival = nearEdge
        ? 1.2 + rng.next() * 1.2
        : Math.max(2.4, 5.8 - difficulty * 0.55 + rng.next());
      targets.push(toward(ids[cursor++]!, edgePoint(firstEdge, rng), collisionPoint, arrival));
      targets.push(toward(ids[cursor++]!, edgePoint(secondEdge, rng), collisionPoint, arrival));
    }

    while (cursor < ids.length) targets.push(randomDecoy(ids[cursor++]!, difficulty, rng));
    const edges = conflictEdges(targets);
    if (desiredConflicts === 0 && edges.length !== 0) continue;
    if (desiredConflicts > 0 && edges.length < desiredConflicts) continue;
    return {
      id,
      seed,
      targets,
      originalEdges: edges,
      minimumCoverSize: minimumVertexCoverSize(ids, edges),
      durationMs: Math.max(5700, 8500 - difficulty * 350),
      startedAt: 0,
    };
  }

  const fallback = [randomDecoy(1, difficulty, rng), randomDecoy(2, difficulty, rng)];
  const fallbackEdges = conflictEdges(fallback);
  return {
    id,
    seed,
    targets: fallback,
    originalEdges: fallbackEdges,
    minimumCoverSize: minimumVertexCoverSize(fallback.map((target) => target.id), fallbackEdges),
    durationMs: 7000,
    startedAt: 0,
  };
}

function distractorsFor(answer: number, rng: SeededRandom): number[] {
  const candidates = [
    answer + 10,
    answer - 10,
    answer + 100,
    answer - 100,
    answer + 1,
    answer - 1,
    Number(String(Math.abs(answer)).split("").reverse().join("")) * Math.sign(answer || 1),
  ].filter((value) => value >= 0 && value !== answer);
  return rng.shuffle([...new Set(candidates)]).slice(0, 3);
}

export function makeRadarMathProblem(
  difficulty: number,
  seed = difficulty * 7919 + 17,
): RadarMathProblem {
  const rng = createRng(seed);
  const operation = difficulty >= 4
    ? rng.pick(["+", "−", "−", "×"] as const)
    : rng.pick(["+", "−", "−"] as const);
  let a: number;
  let b: number;
  let answer: number;
  if (operation === "×") {
    a = rng.int(12, 48);
    b = rng.int(3, 12);
    answer = a * b;
  } else {
    const digits = difficulty <= 2 ? 2 : difficulty === 3 ? 3 : rng.next() < 0.45 ? 4 : 3;
    const min = 10 ** (digits - 1);
    const max = 10 ** digits - 1;
    a = rng.int(min, max);
    b = rng.int(min, max);
    if (operation === "−" && b > a) [a, b] = [b, a];
    answer = operation === "+" ? a + b : a - b;
  }
  const choices = rng.shuffle([answer, ...distractorsFor(answer, rng)]);
  while (choices.length < 4) {
    const candidate = Math.max(0, answer + rng.int(-25, 25));
    if (!choices.includes(candidate)) choices.push(candidate);
  }
  return {
    seed,
    prompt: `${a} ${operation} ${b} = ?`,
    choices: choices.slice(0, 4),
    answerIndex: choices.indexOf(answer),
  };
}
